import { LitElement, html, css, unsafeCSS, property } from "lit-element";

import { reportsApi } from "../../api/reportsApi";
import { seasonsApi } from "../../api/seasonsApi";
import { seriesApi } from "../../api/seriesApi";

import { DropdownModel } from "../../models/components/DropdownModel";
import { SeriesModel } from "../../models/media/SeriesModel";
import { SeasonModel } from "../../models/media/SeasonModel";
import { TemplateModel } from "../../models/template/TemplateModel";
import { ReportFilterModel } from "../../models/report/ReportFilterModel";

import ReportType from "../../enums/report-type";

import Dropdown from "../dropdown/dropdown";

import reportDashboardStyles from "./report-dashboard.scss";
import { DashboardConfigModel } from "../../models/report/DashboardConfigModel";
import { UserModel } from "../../models/dashboard/UserModel";

export default class ReportDashboard extends LitElement {
    @property({ type: Array })
    seriesList: SeriesModel[];

    @property({ type: Array })
    seasons: SeasonModel[];

    @property({ type: Object })
    reportTemplates: TemplateModel[];

    @property({ type: Object })
    reportFilters: ReportFilterModel[];

    @property({ type: Object })
    selectedSeries: SeriesModel;

    @property({ type: Object })
    selectedSeason: SeasonModel;

    @property({ type: Object })
    selectedReportTemplate: TemplateModel;

    @property({ type: Object })
    selectedReportFilter: ReportFilterModel;

    @property({ type: Object })
    selectedUser: UserModel;

    @property({ type: Object })
    dashboardConfig: DashboardConfigModel;

    private _container: HTMLElement;

    private _mediaUrl = "";
    private _reportsUrl = "";
    private _portalUrl = "";
    private _userId = "";
    private _initReport = undefined;

    private _users: UserModel[]
    private _mediaReportsApi: reportsApi;
    private _reportsApi: reportsApi;
    private _seasonsApi: seasonsApi;
    private _seriesApi: seriesApi;

    constructor(
        container: HTMLElement,
        mediaUrl: string,
        reportsUrl: string,
        portalUrl: string,
        userId: string,
        users: UserModel[],
        initReport: any) {
        super();

        this._container = container;
        this._mediaUrl = mediaUrl;
        this._reportsUrl = reportsUrl;
        this._portalUrl = portalUrl;
        this._userId = userId;
        this._users = users.sort((a, b) => this.textSort(a, b, "userName"));;
        this._initReport = initReport;

        this._mediaReportsApi = new reportsApi(this._mediaUrl, this._userId);
        this._reportsApi = new reportsApi(this._reportsUrl, this._userId);

        this.reportFilters = [];
        this.seasons = [];
        this.seriesList = [];
        this.reportTemplates = [];
    }

    async firstUpdated() {

        this.showLoading(true, document.querySelector('#setup-loading-container'));

        this.dashboardConfig = await this._reportsApi.GetDashboardConfigFromReports(this._userId);

        if (this._users) {

            if (this.dashboardConfig && this.dashboardConfig.userId)
                this.selectedUser = this._users.find(x => x.userGuid === this.dashboardConfig.userId);
            else if (this._users.length === 1)
                this.selectedUser = this._users[0];
        }

        this._seasonsApi = new seasonsApi(this._mediaUrl, this.selectedUser?.userGuid ?? this._userId);
        this._seriesApi = new seriesApi(this._mediaUrl, this.selectedUser?.userGuid ?? this._userId);

        let seasonTemplates: TemplateModel[] = [];
        let teamSeasonTemplates: TemplateModel[] = [];
        let playerSeasonTemplates: TemplateModel[] = [];

        let apiCalls = [
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.SeasonReport),
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.TeamSeasonReport),
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.PlayerSeasonReport),
            this._seriesApi.getSeriesList('', [])
        ];

        if (this.dashboardConfig && this.dashboardConfig.seriesId)
            apiCalls.push(this._seasonsApi.GetSeasonsForSeries(this.dashboardConfig.seriesId));

        [seasonTemplates, teamSeasonTemplates, playerSeasonTemplates, this.seriesList, this.seasons] = await Promise.all(apiCalls)

        if (!this.seasons)
            this.seasons = [];

        if (seasonTemplates) {
            this.reportTemplates = this.reportTemplates.concat(seasonTemplates);
        }
        if (teamSeasonTemplates) {
            this.reportTemplates = this.reportTemplates.concat(teamSeasonTemplates);
        }
        if (playerSeasonTemplates) {
            this.reportTemplates = this.reportTemplates.concat(playerSeasonTemplates);
        }

        this.seriesList.sort((a, b) => this.textSort(a, b, "name"));
        this.reportTemplates.sort((a, b) => this.textSort(a, b, "name"));
        this.reportFilters.sort((a, b) => this.textSort(a, b, "name"));

        // Pre select existing options
        if (!this.isEmpty(this.dashboardConfig)) {
            this.selectedReportTemplate = this.reportTemplates.find(x => x.reportTemplateId == this.dashboardConfig.dashboardTemplateId);
            this.selectedSeries = this.seriesList.find(x => x.seriesGuid == this.dashboardConfig.seriesId);
            this.selectedSeason = this.seasons.find(x => x.seasonGuid == this.dashboardConfig.seasonId);

            if (this.selectedReportTemplate && this.selectedSeries && this.selectedSeason)
                await this.loadDashboard();
        }

        this.showLoading(false, document.querySelector('#setup-loading-container'));
    }

    isEmpty(obj) {
        return Object.keys(obj).length === 0;
    }

    textSort(a, b, property) {
        let returnValue = 0;

        if (a[property] < b[property])
            returnValue = -1;
        else
            returnValue = 1;

        return returnValue;
    }

    getUsers() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "-- Users --", null));
        for (let [i, user] of Object.entries(this._users)) {
            dropdownItems.push(new DropdownModel(user.userGuid, user.userName, user));
        }

        let selectedValue = this.selectedUser ? this.selectedUser.userGuid : undefined;

        return new Dropdown(dropdownItems, this.userChange, selectedValue, "User");
    }

    getSeriesList() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "-- Series --", null));
        for (let [i, series] of Object.entries(this.seriesList)) {
            dropdownItems.push(new DropdownModel(series.seriesGuid, series.name, series));
        }

        let selectedValue = this.selectedSeries ? this.selectedSeries.seriesGuid : undefined;

        return new Dropdown(dropdownItems, this.seriesChange, selectedValue, "Series");
    }

    getSeasons() {
        this.seasons.sort((a, b) => this.textSort(a, b, "name"));

        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "-- Seasons --", null));
        for (let [i, season] of Object.entries(this.seasons)) {
            dropdownItems.push(new DropdownModel(season.seasonGuid, season.seasonName, season));
        }

        let selectedValue = this.selectedSeason ? this.selectedSeason.seasonGuid : undefined;

        return new Dropdown(dropdownItems, this.seasonChange, selectedValue, "Season");
    }

    getTemplates() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "-- Reports --", null));
        for (let [i, reportTemplate] of Object.entries(this.reportTemplates)) {
            dropdownItems.push(new DropdownModel(reportTemplate.reportTemplateId, reportTemplate.name, reportTemplate));
        }

        let selectedValue = this.selectedReportTemplate ? this.selectedReportTemplate.reportTemplateId : undefined;

        return new Dropdown(dropdownItems, this.reportTemplateChange, selectedValue, "Report");
    }

    getFilters() {
        let dropdownItems: DropdownModel[] = [];

        dropdownItems.push(new DropdownModel(undefined, "Please select a Report Filter", null));
        for (let [i, reportFilter] of Object.entries(this.reportFilters)) {
            dropdownItems.push(new DropdownModel(reportFilter.reportFilterId, reportFilter.name, reportFilter));
        }

        let selectedValue = this.selectedReportFilter ? this.selectedReportFilter.reportFilterId : undefined;

        return new Dropdown(dropdownItems, this.reportFilterChange, selectedValue, "Report Filter");
    }

    userChange = async (value: string, userModel: UserModel) => {
        this.selectedUser = userModel;

        this.reportTemplates = [];
        this.selectedReportTemplate = undefined;

        this.seriesList = [];
        this.selectedSeries = undefined;

        this.seasons = [];
        this.selectedSeason = undefined;

        if (!userModel)
            return;

        this.showLoading(true, document.querySelector('#setup-loading-container'));

        this._seriesApi = new seriesApi(this._mediaUrl, this.selectedUser?.userGuid ?? this._userId);
        this._seasonsApi = new seasonsApi(this._mediaUrl, this.selectedUser?.userGuid ?? this._userId);

        // Repopulate on User Select
        let apiCalls = [
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.SeasonReport),
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.TeamSeasonReport),
            this._mediaReportsApi.GetTemplatesByReportTypeFromMedia(this.selectedUser?.userGuid ?? this._userId, ReportType.PlayerSeasonReport),
            this._seriesApi.getSeriesList('', [])
        ];

        let seasonTemplates: TemplateModel[] = [];
        let teamSeasonTemplates: TemplateModel[] = [];
        let playerSeasonTemplates: TemplateModel[] = [];

        [seasonTemplates, teamSeasonTemplates, playerSeasonTemplates, this.seriesList] = await Promise.all(apiCalls);

        if (seasonTemplates) {
            this.reportTemplates = this.reportTemplates.concat(seasonTemplates);
        }
        if (teamSeasonTemplates) {
            this.reportTemplates = this.reportTemplates.concat(teamSeasonTemplates);
        }
        if (playerSeasonTemplates) {
            this.reportTemplates = this.reportTemplates.concat(playerSeasonTemplates);
        }

        this.seriesList.sort((a, b) => this.textSort(a, b, "name"));
        this.reportTemplates.sort((a, b) => this.textSort(a, b, "name"));
        this.reportFilters.sort((a, b) => this.textSort(a, b, "name"));

        this.showLoading(false, document.querySelector('#setup-loading-container'));

    }

    seriesChange = async (value: string, seriesModel: SeriesModel) => {
        this.selectedSeason = undefined;
        this.selectedSeries = seriesModel;

        if (seriesModel) {
            this.showLoading(true, document.querySelector('#setup-loading-container'));
            this.seasons = await this._seasonsApi.GetSeasonsForSeries(seriesModel.seriesGuid);
            this.showLoading(false, document.querySelector('#setup-loading-container'));
        }
    }

    seasonChange = async (value: string, seasonModel: SeasonModel) => {
        this.selectedSeason = seasonModel;
    }

    reportTemplateChange = (value: string, reportTemplate: TemplateModel) => {
        this.selectedReportTemplate = reportTemplate;
    }

    reportFilterChange = (value: string, reportFilterModel: ReportFilterModel) => {
        this.selectedReportFilter = reportFilterModel;
    }

    saveDashboard = async () => {
        document.querySelector('#error-container').innerHTML = '';
        let errorMessage = this.createErrorMessage();

        if (errorMessage) {
            errorMessage = "Please ensure the following are selected:" + errorMessage;
            document.querySelector('#error-container').innerHTML = `<span class="text-danger">${errorMessage}</span>`;
            this.showLoading(false, document.querySelector('#setup-loading-container'));
            return;
        }

        if (!this.dashboardConfig) {
            this.dashboardConfig = <DashboardConfigModel>{
                userId: '',
                dashboardTemplateId: '',
                seriesId: '',
                seasonId: '',
            };
        }

        this.dashboardConfig.userId = this.selectedUser?.userGuid ?? this._userId;
        this.dashboardConfig.dashboardTemplateId = this.selectedReportTemplate.reportTemplateId;
        this.dashboardConfig.seriesId = this.selectedSeries.seriesGuid;
        this.dashboardConfig.seasonId = this.selectedSeason.seasonGuid;

        await this._reportsApi.postDashboardConfig(this._userId, this.dashboardConfig);
    }

    loadDashboard = async () => {
        document.querySelector('#error-container').innerHTML = '';
        let errorMessage = this.createErrorMessage();

        if (errorMessage) {
            errorMessage = "Please ensure the following are selected:" + errorMessage;
            document.querySelector('#error-container').innerHTML = `<span class="text-danger">${errorMessage}</span>`;
            this.showLoading(false, document.querySelector('#setup-loading-container'));
            return;
        }

        let loader = document.createElement('span');
        loader.innerHTML = 'Loading Dashboard...';

        this._initReport(
            this._mediaUrl,
            this.selectedUser?.userGuid ?? this._userId,
            this._container,
            this.selectedReportTemplate.reportId ?? "",
            "",
            this.selectedReportTemplate.reportTemplateId,
            loader,
            undefined,
            this.selectedReportTemplate.reportType,
            false,
            false,
            (this.selectedSeason ? this.selectedSeason.seasonGuid : undefined),
            false,
            undefined,
            true,
            this.selectedSeries.seriesGuid,
            (this.selectedReportFilter ? this.selectedReportFilter.reportFilterId : ""),
            this.selectedReportTemplate.isMultiTemplate);
    }

    showLoading(isLoading: boolean, container: HTMLElement) {
        if (isLoading) {
            container.innerHTML = "Loading...";
        }
        else {
            container.innerHTML = "";
        }
    }

    createErrorMessage() {
        let errorMessage: string = '';

        let userId = this.selectedUser?.userGuid ?? this._userId;

        if (!userId) {
            errorMessage += " a User";
        }

        if (!this.selectedReportTemplate) {
            errorMessage += " a Report Template";
        }

        if (this.selectedReportTemplate) {
            switch (this.selectedReportTemplate.reportType) {
                case ReportType.SeasonReport:
                case ReportType.TeamSeasonReport:
                case ReportType.PlayerSeasonReport:
                    if (!this.selectedSeries) {
                        errorMessage += " a Series;";
                    }
                    if (!this.selectedSeason) {
                        errorMessage += " a Season;";
                    }
                    break;
            }
        }

        return errorMessage;
    }

    createRenderRoot() {
        return this;
    }

    render() {

        return [
            html`
            <style>
                ${css`${unsafeCSS(reportDashboardStyles)}`}                     
            </style>

            <div class="container">                

                <div class="row">  
            
                    ${(this._users.length > 0) ? html`<div class="col"> ${this.getUsers()} </div>` : html`<div class="col"></div>`}

                    ${(this.reportTemplates.length > 0) ? html`<div class="col"> ${this.getTemplates()} </div>` : html`<div class="col"></div>`}

                    ${(this.seriesList.length > 0) ? html`<div class="col"> ${this.getSeriesList()} </div>` : html`<div class="col"></div>`}

                    ${(this.seasons.length > 0) ? html`<div class="col"> ${this.getSeasons()} </div>` : html`<div class="col"></div>`}

                    <div class="col dashboard-btn-col">
                    ${this.selectedReportTemplate && this.selectedSeries && this.selectedSeason ? html`<button class="btn btn-info wide-element" @click=${this.saveDashboard}>Save Dashboard</button>` : ''}
                    </div>

                    <div class="col dashboard-btn-col">
                    ${this.selectedReportTemplate && this.selectedSeries && this.selectedSeason ? html`<button class="btn btn-info wide-element" @click=${this.loadDashboard}>Load Dashboard</button>` : ''}
                    </div>
                </div>

                <div class="row">
                    <div class="col" id="error-container"></div>
                </div>

                <div class="row">
                    <div class="col loading-container" id="setup-loading-container"></div>
                </div>       

            </div>`
        ];
    }
}

window.customElements.define('stratus-stats-report-dashboard', ReportDashboard as any);